import React from 'react';
import { FiEye, FiTrash2, FiList, FiUserPlus, FiMessageSquare } from 'react-icons/fi';

const TaskRow = ({
    task,
    user,
    isManager,
    activeTab,
    onView,
    onComments,
    onReassign,
    onApprove,
    onVerify,
    onDelete,
    onStatusChange,
    formatDate,
    formatDateTime,
    getPriorityColor,
    getStatusBadge,
    getApprovalStatusBadge,
    isOverdue
}) => {
    // Check if current user is the task creator (assignee)
    const taskCreatorId = task.assignedBy?._id || task.assignedBy;
    const currentUserId = user?._id || user?.id;
    const isTaskCreator = taskCreatorId && currentUserId &&
        (taskCreatorId.toString() === currentUserId.toString());

    // Mirrors canModifyTask() in the backend's utils/taskScope.js: creator,
    // assignee or system_admin. A department head sees rows on the Department tab
    // that they may read and comment on but not change, so the controls below are
    // hidden rather than left to fail with a 403.
    const isAssignee = (Array.isArray(task.assignedTo) ? task.assignedTo : [task.assignedTo])
        .filter(Boolean)
        .some(a => currentUserId && String(a._id || a) === String(currentUserId));
    const canModify = user?.role === 'system_admin' || isTaskCreator || isAssignee;


    return (
        <tr key={task._id} className={isOverdue(task.dueDate, task.status) ? 'overdue-row' : ''}>
            <td className="td-actions-icons">
                <div className="row-action-icons">
                    <button type="button" onClick={() => onView(task)} className="btn-icon btn-icon-view" title="View" aria-label="View">
                        <FiEye size={18} />
                    </button>
                    <button type="button" onClick={() => onComments(task)} className="btn-icon btn-icon-comments" title="Comments" aria-label="Comments">
                        <FiMessageSquare size={18} />
                        {task.unreadCommentCount > 0 && (
                            <span className="comment-count-badge">{task.unreadCommentCount}</span>
                        )}
                    </button>
                    {!task.parentTask && (
                        <>
                            <button type="button" onClick={() => onView(task)} className="btn-icon btn-icon-subtasks" title="View Task & Subtasks" aria-label="View Task & Subtasks">
                                <FiList size={18} />
                            </button>
                            {canModify && (
                                <button type="button" onClick={() => onReassign(task)} className="btn-icon btn-icon-edit-inline" title="Reassign" aria-label="Reassign">
                                    <FiUserPlus size={18} />
                                </button>
                            )}
                        </>
                    )}
                    {isManager && activeTab === 'assigned-to-me' && task.approvalStatus === 'pending' && (
                        <>
                            <button type="button" onClick={() => onApprove(task, 'approve')} className="btn-approve btn-action-inline">Approve</button>
                            <button type="button" onClick={() => onApprove(task, 'reject')} className="btn-reject btn-action-inline">Reject</button>
                        </>
                    )}
                    {isTaskCreator && task.status === 'completed' && (
                        <>
                            {!task.verified ? (
                                <button type="button" onClick={() => onVerify(task, 'verify')} className="btn-verify btn-action-inline">✓ Verify</button>
                            ) : (
                                <button type="button" onClick={() => onVerify(task, 'unverify')} className="btn-unverify btn-action-inline">✗ Unverify</button>
                            )}
                        </>
                    )}
                    {user?.role === 'system_admin' && (
                        <button type="button" onClick={() => onDelete(task._id)} className="btn-icon btn-icon-delete" title="Delete" aria-label="Delete">
                            <FiTrash2 size={18} />
                        </button>
                    )}
                </div>
            </td>
            <td>
                <span className="task-id-badge">{task.taskId || 'N/A'}</span>
            </td>
            <td className="task-title-cell">
                <strong>{task.title}</strong>
                {task.source === 'ms-todo' && (
                    <span className="outlook-badge" title="Imported from Microsoft To Do">
                        📧 Outlook
                    </span>
                )}
                {task.routineTask && (
                    <span className="routine-badge" title="Auto-created from a routine task">
                        🔁 Routine
                    </span>
                )}
                {task.verified && (
                    <span className="verified-badge" title={`Verified by ${task.verifiedByName}`}>
                        ✓ Verified
                    </span>
                )}
            </td>
            {activeTab === 'all-tasks' ? (
                <>
                    <td>{Array.isArray(task.assignedToName) ? task.assignedToName.join(', ') : task.assignedToName}</td>
                    <td>{task.assignedByName}</td>
                </>
            ) : (
                <td>
                    {activeTab === 'assigned-by-me'
                        ? (Array.isArray(task.assignedToName) ? task.assignedToName.join(', ') : task.assignedToName)
                        : task.assignedByName
                    }
                </td>
            )}
            <td>
                <span className="priority-badge" style={{ backgroundColor: getPriorityColor(task.priority) }}>
                    {task.priority}
                </span>
            </td>
            {/* Show approval status column for managers in assigned-to-me tab OR for everyone in assigned-by-me tab */}
            {((isManager && activeTab === 'assigned-to-me') || activeTab === 'assigned-by-me') && (
                <td>
                    <span className={`approval-badge ${getApprovalStatusBadge(task.approvalStatus).class}`}>
                        {getApprovalStatusBadge(task.approvalStatus).label}
                    </span>
                </td>
            )}
            <td>
                {isManager && activeTab === 'assigned-to-me' ? (
                    // For managers in approval tab, show status as read-only badge
                    <span className={`status-badge ${getStatusBadge(task.status).class}`}>
                        {getStatusBadge(task.status).label}
                    </span>
                ) : !canModify ? (
                    // Visible through department scope only: read-only status
                    <span className={`status-badge ${getStatusBadge(task.status).class}`} title="You can view and comment on this task, but not change it">
                        {getStatusBadge(task.status).label}
                    </span>
                ) : task.verified && !isTaskCreator && user?.role !== 'system_admin' ? (
                    // If task is verified and user is not the creator or admin, show locked status
                    <span className={`status-badge ${getStatusBadge(task.status).class}`} title="Status locked - Task is verified">
                        {getStatusBadge(task.status).label} 🔒
                    </span>
                ) : (
                    // For others, show editable dropdown
                    <select 
                        value={task.status} 
                        onChange={(e) => onStatusChange(task._id, e.target.value)}
                        className={`status-select ${getStatusBadge(task.status).class}`}
                        disabled={task.verified && !isTaskCreator && user?.role !== 'system_admin'}
                        title={task.verified && !isTaskCreator && user?.role !== 'system_admin' ? 'Status locked - Task is verified' : ''}
                    >
                        <option value="todo">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="review">Review</option>
                        <option value="completed">Completed</option>
                    </select>
                )}
            </td>
            <td style={{ fontSize: '0.9em', color: '#666' }}>{formatDateTime(task.createdAt)}</td>
            <td>{formatDate(task.dueDate)}</td>
        </tr>
    );
};

export default TaskRow;
